import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert, Typography,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

export default function PriceUI() {

  const [prices, setPrices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [file, setFile] = useState(null);

  const loadPrices = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances. priceApi.get(`/?page=${pageNum}&size=${size}`);
      console.log("API Response:", res.data); // Add this line
      const result = res.data;
      const payload = result.data || {};
      setPrices(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching prices", err);
    }
  };

  useEffect(() => { loadPrices(); }, [page, rowsPerPage]);

  const handleOpen = (price = {}) => {
    const transformedPrice = {
      ...price,
      startDate: price.startDate
        ? convertToInputDateFormat(price.startDate)
        : new Date().toISOString().split("T")[0],
      endDate: price.endDate
        ? convertToInputDateFormat(price.endDate)
        : new Date().toISOString().split("T")[0],
    };
    setForm(transformedPrice);
    setEditing(!!price.id);
    setOpen(true);
  };

  function formatDate(inputDate) {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  }

  function convertToInputDateFormat(dateStr) {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }


  const handleClose = () => { setForm({}); setOpen(false); setFile(null);};

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  
  const handleSave = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      if (file) {
        // ✅ Validate file type for price uploads
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
          "text/csv" // .csv
        ];

        if (!allowedTypes.includes(file.type)) {
          setSnackbar({
            open: true,
            message: "Only Excel or CSV files are allowed for price uploads!",
            severity: "error"
          });
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        // ✅ Upload price file
        await axiosInstances. priceApi.post(`/uploadPrices`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSnackbar({
          open: true,
          message: "Price file uploaded successfully!",
          severity: "success"
        });

      } else {
        // ✅ Manual price entry
        const pricePayload = {
          ...form,
          startDate: formatDate(form.startDate),
          endDate: formatDate(form.endDate),// assuming price is a number
        };

        if (editing) {
          await axiosInstances. priceApi.post(`/update`, pricePayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({
            open: true,
            message: "Price updated successfully!",
            severity: "success"
          });
        } else {
          await axiosInstances. priceApi.post(`/save`, pricePayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({
            open: true,
            message: "Price saved successfully!",
            severity: "success"
          });
        }
      }

      loadPrices();   // 🔁 Refresh price list
      handleClose();  // ❌ Close modal/dialog
      setFile(null);  // 🧹 Clear file input

    } catch (err) {
      if (err.response && err.response.status === 409) {
        setSnackbar({
          open: true,
          message: "Duplicate price entry",
          severity: "error"
        });
      } else if (err.response && err.response.status === 401) {
        setSnackbar({
          open: true,
          message: "Unauthorized - please log in as admin",
          severity: "error"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Something went wrong while saving the price!",
          severity: "error"
        });
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };


  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances. priceApi.post(`/delete`, { id });
        loadPrices();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Price Management</Typography>
      <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
        Add Price
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Active</TableCell>
              {/* <TableCell>Status</TableCell> */}
              <TableCell>Product</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.map((price, index) => (
              <TableRow key={price.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{price.id}</TableCell>
                <TableCell>{price.amount}</TableCell>
                <TableCell>{price.currency}</TableCell>
                <TableCell>{price.startDate}</TableCell>
                <TableCell>{price.endDate}</TableCell>
                <TableCell>{price.priceType}</TableCell>
                <TableCell>{price.isActive ? "Yes" : "No"}</TableCell>
                {/* <TableCell>{price.status}</TableCell> */}
                <TableCell>{price.product}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(price)}>
                    <Edit />
                  </IconButton>
                  {pendingDeletes[price.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(price.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(price.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {prices.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">No prices found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit Price" : "Add Price"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Amount" name="amount" type="number" value={form.amount || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Currency" name="currency" value={form.currency || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Start Date" name="startDate" type="date" value={form.startDate || new Date().toISOString().split("T")[0]} onChange={handleChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" label="End Date" name="endDate" type="date" value={form.endDate || new Date().toISOString().split("T")[0]} onChange={handleChange} InputLabelProps={{ shrink: true }} />
          {/* <TextField fullWidth margin="dense" label="Price Type" name="priceType" value={form.priceType || ""} onChange={handleChange} /> */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Price Type</InputLabel>
            <Select
              name="priceType"
              value={form.priceType || ""}
              onChange={handleChange}
              label="Price Type"
            >
              <MenuItem value="REGULAR">Regular</MenuItem>
              <MenuItem value="DISCOUNTED">Discounted</MenuItem>
              <MenuItem value="SEASONAL">Seasonal</MenuItem>
              <MenuItem value="OFFER">Offer</MenuItem>
              <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
              <MenuItem value="WHOLESALE">Wholesale</MenuItem>
              <MenuItem value="RETAIL">Retail</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={form.isActive || false} onChange={handleChange} name="isActive" />}
            label="Active"
          />
          <TextField fullWidth margin="dense" label="Product" name="product" value={form.product || ""} onChange={handleChange} />
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
